import type { CSSProperties, HTMLAttributes, ReactNode } from "react";
import { cx } from "../cx";

/** Cabecera de página: título + meta (badges) + acciones. */
export function PageHead({ title, meta, actions, skeleton }: { title: ReactNode; meta?: ReactNode | undefined; actions?: ReactNode | undefined; skeleton?: boolean | undefined }) {
  return (
    <div className="sk-pagehead">
      <div>
        {skeleton ? <Skeleton style={{ width: "16rem", maxWidth: "100%", height: "1.4em" }} /> : <h1 className="sk-h2">{title}</h1>}
        {meta !== undefined && !skeleton && <div className="sk-ph-meta">{meta}</div>}
        {skeleton && <Skeleton style={{ width: "24rem", maxWidth: "100%", height: ".9em", marginTop: "var(--sk-sp-3)" }} />}
      </div>
      {actions !== undefined && <div className="sk-ph-actions">{actions}</div>}
    </div>
  );
}
export type BannerTone = "danger" | "info" | "warn" | "ok";
const GLYPH: Record<BannerTone, string> = { danger: "!", info: "ℹ", warn: "△", ok: "✓" };
/**
 * Banner del sistema. El de peligro se anuncia solo (`role=alert`); con
 * `live={false}` no lleva rol, para la pantalla donde una región viva ya
 * anuncia lo mismo (un solo anuncio, como la canónica del simulador).
 */
export function Banner({ tone, children, style, className, live, role }: { tone: BannerTone; children: ReactNode; style?: CSSProperties | undefined; className?: string | undefined; live?: boolean | undefined; role?: "note" | undefined }) {
  return (
    <div className={cx("sk-banner", `sk-banner--${tone}`, className)} role={role ?? (tone === "danger" && live !== false ? "alert" : undefined)} style={style}>
      <span>{GLYPH[tone]}</span>
      <span>{children}</span>
    </div>
  );
}
/** Estado vacío: glifo (marca), título en negrita, explicación, acción opcional.
    `role="status"`: un error que se anuncia sin interrumpir («No llego a los contadores de uso»). */
export function Empty({ glyph, title, children, inline, solid, style, role }: { glyph?: ReactNode | undefined; title: ReactNode; children?: ReactNode | undefined; inline?: boolean | undefined; solid?: boolean | undefined; style?: CSSProperties | undefined; role?: "status" | "alert" | undefined }) {
  return (
    <div className={cx("sk-empty", inline && "sk-empty--inline", solid && "sk-empty--solid")} style={style} role={role}>
      {glyph !== undefined && <span className="sk-empty-glyph">{glyph}</span>}
      {/* el espacio lo pone el sistema: en el vacío INLINE el <b> va en línea
          y sin él la explicación se pega al título («… aún.Los criterios…») */}
      <b>{title}</b>
      {children !== undefined && " "}
      {children}
    </div>
  );
}
/**
 * Mensaje de error suelto (`p.sk-error-msg`), fuera de un campo: la frase
 * de un fichero que no se lee bajo la zona de soltar, el problema de un
 * mando bajo el mando. `alert`: se anuncia al aparecer (nace de una acción
 * que no mueve el foco).
 */
export function ErrorMsg({ id, alert, children, style, className }: { id?: string | undefined; alert?: boolean | undefined; children: ReactNode; style?: CSSProperties | undefined; className?: string | undefined }) {
  return <p className={cx("sk-error-msg", className)} id={id} role={alert === true ? "alert" : undefined} style={style}><span aria-hidden="true">!</span><span>{children}</span></p>;
}
export function Skeleton({ style, className }: { style?: CSSProperties | undefined; className?: string | undefined }) {
  return <div className={cx("sk-skeleton", className)} style={style} />;
}
/** Marca un bloque como datos desactualizados (error de red con datos previos). */
export function Stale({ stale, className, ...rest }: { stale: boolean } & HTMLAttributes<HTMLDivElement>) {
  return <div className={cx(stale && "sk-stale", className)} {...rest} />;
}
